/**
 * Attachment upload: the network half.
 *
 * Moved from components/text-widget/utils/input-utils.ts (InputFileUtils).
 * Three calls, in the order the widget makes them:
 *
 *   1. POST  /chat-widget/attachments-url/...          → one signed PUT URL per file
 *   2. PUT   <signed url>                               → the bytes, straight to storage
 *   3. PATCH /chat-widget/chat-widget-attachments/...  → tell the backend what landed
 *
 * plus POST /chat-widget/public/log for a rejected file, so the sub-account's
 * upload failures show up in reporting. None of these carry the bearer token;
 * the widget never sent one and the endpoints are public per widget id.
 *
 * The widget's InputFileUtils now calls these, so there is one implementation.
 * What stayed behind in the widget is presentation and session persistence:
 * the attached-file chips, the notification toasts, and the sessionStorage
 * copy that survives a reload.
 */

import { FILE_UPLOAD_CONSTANTS } from './upload-constants';
import { FileUploadError } from './uploader';
import { label } from '../host/i18n';
import { logError } from '../log';

/** The three fields the upload endpoints are keyed on. */
export interface UploadTarget {
  locationId: string;
  marketplaceURL: string;
  widgetId?: string;
}

export interface UploadMeta {
  id: string;
  name: string;
  contentType: string;
}

/** One entry of the `urls` array the attachments-url endpoint returns. */
export interface SignedUpload {
  uploadUrl: string;
  uniqueName?: string;
  downloadUrl?: string;
  path?: string;
  /**
   * Headers the PUT must carry for the signature to match. The server adds
   * `x-goog-content-length-range` here once it signs URLs with a size ceiling;
   * sent verbatim, never hard-coded on this side.
   */
  headers?: Record<string, string>;
}

/** What a finished upload is known by. `downloadUrl` is what a message carries. */
export interface StoredFile {
  id: string;
  uploadUrl: string;
  uniqueName: string;
  downloadUrl: string;
  path: string;
}

const uploadFailed = () =>
  new FileUploadError(label('upload_failed', 'File upload failed. Please try again.'), 'UPLOAD_FAILED');

/** Step 1: one signed PUT URL per file, in the order the metas were given. */
export async function requestUploadUrls(
  target: UploadTarget,
  conversationId: string | undefined,
  metas: UploadMeta[],
): Promise<SignedUpload[]> {
  const apiUrl = FILE_UPLOAD_CONSTANTS.API_ENDPOINTS.getCreateAttachmentsUrl(
    target.marketplaceURL,
    target.locationId,
    target.widgetId,
    conversationId,
  );
  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attachments: metas }),
  });
  if (!response.ok) {
    logError('attachments: the server would not issue upload URLs', undefined, { status: response.status });
    throw uploadFailed();
  }
  const { urls } = await response.json();
  if (!Array.isArray(urls) || urls.length !== metas.length) {
    logError('attachments: upload URL count does not match the files asked for', undefined, {
      asked: metas.length,
      got: Array.isArray(urls) ? urls.length : typeof urls,
    });
    throw uploadFailed();
  }
  return urls as SignedUpload[];
}

export interface StorageUpload {
  /** Resolves with the stored file; rejects with a FileUploadError. */
  done: Promise<StoredFile>;
  /** Stops the PUT; `done` rejects with code UPLOAD_ABORTED. */
  abort: () => void;
}

/**
 * Step 2: PUT the bytes to the signed URL. XHR rather than fetch because
 * upload progress is the whole reason a host shows a bar.
 */
export function uploadToStorage(
  file: File,
  meta: UploadMeta,
  signed: SignedUpload,
  onProgress?: (progress: number) => void,
): StorageUpload {
  let xhr: XMLHttpRequest | null = null;
  const done = new Promise<StoredFile>((resolve, reject) => {
    // Constructed inside the executor so an environment without XHR turns
    // into a failed upload for this file, not an exception for the caller —
    // which is what the widget's own code did.
    try {
      xhr = new XMLHttpRequest();
    } catch (e) {
      logError('attachments: no XMLHttpRequest in this environment', e);
      reject(uploadFailed());
      return;
    }
    xhr.open('PUT', signed.uploadUrl, true);
    xhr.setRequestHeader('Content-Type', file.type);
    for (const [name, value] of Object.entries(signed.headers || {})) xhr.setRequestHeader(name, value);
    if (onProgress) {
      xhr.upload.onprogress = (ev: ProgressEvent) => {
        if (ev.lengthComputable) onProgress(ev.loaded / ev.total);
      };
    }
    const request = xhr;
    const fail = () => {
      logError('attachments: storage rejected the upload', undefined, { name: meta.name, status: request.status });
      reject(uploadFailed());
    };
    xhr.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        if (onProgress) onProgress(1);
        resolve({
          id: meta.id,
          uploadUrl: signed.uploadUrl,
          uniqueName: signed.uniqueName || signed.uploadUrl.split('/').pop() || meta.name,
          downloadUrl: signed.downloadUrl || signed.uploadUrl.split('?')[0],
          path: signed.path || '',
        });
      } else {
        fail();
      }
    };
    xhr.onerror = fail;
    xhr.onabort = () => reject(new FileUploadError(label('upload_aborted', 'Upload aborted'), 'UPLOAD_ABORTED'));
    xhr.send(file);
  });
  return { done, abort: () => xhr?.abort() };
}

export interface StoredAttachmentRecord {
  id: string;
  name: string;
  fileSize: number;
  downloadUrl: string;
  path: string;
}

/**
 * Step 3: tell the backend which files now sit in storage for this
 * conversation. Only possible once a conversation exists; a file attached
 * before the first message is announced by the message itself.
 *
 * `downloadUrl` here is the signed upload URL, as the widget has always sent
 * it — the backend strips the query itself.
 */
export async function notifyBackend(
  target: UploadTarget,
  conversationId: string,
  records: StoredAttachmentRecord[],
): Promise<void> {
  const patchUrl = FILE_UPLOAD_CONSTANTS.API_ENDPOINTS.getChatWidgetAttachmentsUrl(
    target.marketplaceURL,
    target.locationId,
    target.widgetId,
    conversationId,
  );
  const unique = records.filter((a, idx, arr) => arr.findIndex(b => b.id === a.id) === idx);
  const response = await fetch(patchUrl, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attachments: unique }),
  });
  if (!response.ok) {
    throw new Error(
      label('backend_notification_failed', 'Backend notification failed.') + `: ${response.status}`,
    );
  }
}

export interface UploadErrorReport {
  chatType?: string;
  fileType: string;
  fileSize: number;
  /** A FileUploadError code, or UPLOAD_FAILED for anything else. */
  errorStatusCode: string;
}

/**
 * Reporting only. Never throws: a failure to log a failure is written to the
 * console and otherwise ignored, so it cannot mask the error being reported.
 */
export async function logFileUploadError(target: UploadTarget, report: UploadErrorReport): Promise<void> {
  try {
    const logUrl = FILE_UPLOAD_CONSTANTS.API_ENDPOINTS.getFileUploadLogUrl(target.marketplaceURL, target.locationId);
    await fetch(logUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        chatType: report.chatType || 'liveChat',
        widgetId: target.widgetId,
        fileType: report.fileType,
        fileSize: report.fileSize,
        errorStatusCode: report.errorStatusCode,
      }),
    });
  } catch (e) {
    logError('attachments: could not report the upload failure', e);
  }
}

export const uploadErrorCode = (error: unknown): string =>
  error instanceof FileUploadError ? error.code : 'UPLOAD_FAILED';
